import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { setQueryApplicationsSummary } from '../../hooks/queries/summary';
import { useSelector } from '../../selectors/useSelector';

import SelectAvailableActions from '../../../../src/components/modals/AddPropertyModal/components/SelectAvailableActions';
import ApplicationsModule from '../../components/ApplicationsModule';
import ConnectedModuleCard from '../../components/ConnectedModuleCard';
import InquiriesModule from '../../components/InquiriesModule';
import InterestedRenters from '../../components/InterestedRenters';
import LeasesModule from '../../components/LeasesModule';
import ListingModule from '../../components/ListingModule';
import NextStepsModule from '../../components/NextStepsModule';
import PaymentsModule from '../../components/PaymentsModule';
import SimpleCard from '../../components/SimpleCard';
import TenantsModule from '../../components/TenantsModule';
import UpcomingToursModule from '../../components/UpcomingToursModule';

interface LocationState {
  showSuccessModal?: boolean;
}

const OverviewPage = () => {
  const queryClient = useQueryClient();
  const { state: locationState = {} } = useLocation();

  const initialRender = useRef(true);

  const {
    active,
    isApplicationsAccepted,
    isApplicationsRestricted,
    isLeasesRestricted,
    isPaymentsRestricted,
    isEnterpriseCustomer,
    isRoomForRent,
    listingAlias,
    user,
  } = useSelector((state) => {
    return {
      active: state.properties.currentProperty.active,
      isApplicationsAccepted: state.properties.currentProperty.isApplicationsAccepted,
      isApplicationsRestricted:
        state?.properties?.currentProperty?.restrictions?.applications?.isAccessRestricted || false,
      isLeasesRestricted: state.properties.currentProperty.restrictions.isLeasesRestricted,
      isPaymentsRestricted: state.properties.currentProperty.restrictions.isPaymentsRestricted,
      isEnterpriseCustomer: state.user.isEnterpriseCustomer,
      isRoomForRent: propertyUtils.isRoomForRent({ currentProperty: state.properties.currentProperty }),
      listingAlias: state.properties.currentProperty.listingAlias,
      state: state.properties.currentProperty.state,
      zgApi: state.env.zgApi,
      user: state.user,
    };
  }, shallowEqual);

  const nextStepGridOffset = { base: '-16px', md: '-24px', xl: 0 };
  const { showSuccessModal } = locationState as LocationState;
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(showSuccessModal ?? false);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    syncWithQueryCache({ isApplicationsAccepted });
  }, [isApplicationsAccepted]);

  const syncWithQueryCache = ({ isApplicationsAccepted }: { isApplicationsAccepted: boolean }) => {
    setQueryApplicationsSummary({
      config: {
        queryClient,
        listingAlias,
        getActive: true,
      },
      payload: {
        isApplicationsAccepted,
      },
    });
  };

  const renderLoadingModule = (i: number) => (
    <Box data-testid="LoadingModuleContainer" css={{ marginBottom: '400' }}>
      <ConnectedModuleCard key={i} title="loading" isLoading={true}>
        <SimpleCard isLoading={true} />
      </ConnectedModuleCard>
    </Box>
  );

  const renderActiveModules = useMemo(() => {
    if (!listingAlias) {
      return [...Array(5)].map(renderLoadingModule);
    }

    return (
      <>
        <Box css={{ marginBottom: '400' }}>
          <ListingModule />
        </Box>

        <Box css={{ marginBottom: '400' }}>
          <TenantsModule />
        </Box>

        {isEnterpriseCustomer ? (
          <Box data-testid="InquiriesContainer" css={{ marginBottom: '400' }}>
            <InquiriesModule />
          </Box>
        ) : (
          isRoomForRent !== true && (
            <Box data-testid="InterestedRentersContainer" css={{ marginBottom: '400' }}>
              <InterestedRenters />
            </Box>
          )
        )}

        {isApplicationsRestricted !== true && (
          <Box data-testid="ApplicationsModuleContainer" css={{ marginBottom: '400' }}>
            <ApplicationsModule />
          </Box>
        )}

        {isLeasesRestricted !== true && (
          <Box data-testid="LeasesModuleContainer" css={{ marginBottom: '400' }}>
            <LeasesModule />
          </Box>
        )}

        {isPaymentsRestricted !== true && (
          <Box data-testid="PaymentsModuleContainer" css={{ marginBottom: '400' }}>
            <PaymentsModule />
          </Box>
        )}
      </>
    );
  }, [
    isApplicationsRestricted,
    isEnterpriseCustomer,
    isLeasesRestricted,
    isPaymentsRestricted,
    isRoomForRent,
    listingAlias,
  ]);

  const renderInactiveModules = useMemo(() => {
    // if propertyDetailsOverview is loading, fill column with skeleton modules
    if (!listingAlias) {
      return [...Array(5)].map(renderLoadingModule);
    }

    return (
      <>
        <Box css={{ marginBottom: '400' }}>
          <TenantsModule />
        </Box>

        {isLeasesRestricted !== true && (
          <Box data-testid="LeasesModuleContainer" css={{ marginBottom: '400' }}>
            <LeasesModule />
          </Box>
        )}

        {isPaymentsRestricted !== true && (
          <Box data-testid="PaymentsModuleContainer" css={{ marginBottom: '400' }}>
            <PaymentsModule />
          </Box>
        )}

        <Box css={{ marginBottom: '400' }}>
          <ListingModule />
        </Box>
      </>
    );
  }, [isLeasesRestricted, isPaymentsRestricted, listingAlias]);

  return (
    <div data-testid="OverviewPage">
      {isSuccessModalOpen && (
        <Modal
          open={isSuccessModalOpen}
          onOpenChange={(open) => !open && setIsSuccessModalOpen(false)}
          scrollDocument={false}
          size={{ base: 'fullScreen', md: 'lg' }}
          header={<Heading level={2}>Zillow Rental Manager</Heading>}
          body={<SelectAvailableActions listingAlias={listingAlias} triggerObject="zrm_user_onboarding" user={user} />}
        />
      )}
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0,1fr))', gap: { xl: '400' } }}>
        <Box data-testid="LeftColumn" css={{ gridColumn: { base: '1/13', xl: '1/8' } }}>
          {active ? renderActiveModules : renderInactiveModules}
        </Box>

        {/* override margin left and right to display as fluid */}
        <Box
          data-testid="RightColumn"
          css={{
            gridColumn: { base: '1/13', xl: '8/13' },
            gridRowStart: { base: '1', xl: 'inherit' },
            marginLeft: nextStepGridOffset,
            marginRight: nextStepGridOffset,
            marginBottom: '400',
          }}
        >
          {/* if propertyDetailsOverview is loading, fill column with skeleton modules */}
          {listingAlias ? (
            <>
              <UpcomingToursModule />
              <NextStepsModule />
            </>
          ) : (
            [...Array(2)].map(renderLoadingModule)
          )}
        </Box>
      </Box>
    </div>
  );
};

export default memo(OverviewPage);
